import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    fetchPlanManagerClientConfig,
    fetchPlanManagerClientConfigs,
    fetchPlanManagerHealthEdgeConfig,
    fetchPlanManagerHealthEdgePlansBatch,
    fetchPlanManagerPlansBatch,
} from "../../../utils/apiUtils.js";
import { PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS, PLAN_MANAGER_QUERY_KEYS } from "../common/constants";
import {
    cycleNetworkListSort,
    filterNetworkConfigsBySearchCriteria,
    mapClientConfigsToNetworkRows,
    sortNetworkConfigs,
} from "../../../utils/planManager/networkConfigurationUtils.js";
import { loadAllPlansGrids } from "../../../utils/planManager/planConfigurationUtils.js";


/**
 * Delay before search text is applied to the table, matching NetworkList.vue.
 * @constant {number}
 */
export const NETWORK_SEARCH_DEBOUNCE_MS = 400;


/**
 * Buefy `default-sort="clientName"` — first paint is Client Name, ascending.
 * @constant {string}
 */
export const DEFAULT_NETWORK_SORTED_FIELD = "clientName";

const NETWORKS_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

/**
 * Ephemeral UI state and handlers for the networks list screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function useNetworks() {
    const [searchCriteria, setSearchCriteria] = useState("");
    const [appliedSearchCriteria, setAppliedSearchCriteria] = useState("");
    const [sortedField, setSortedField] = useState(DEFAULT_NETWORK_SORTED_FIELD);
    const [isSortDescending, setIsSortDescending] = useState(false);

    useEffect(() => {
        const debounceTimeoutId = setTimeout(() => {
            setAppliedSearchCriteria(searchCriteria);
        }, NETWORK_SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(debounceTimeoutId);
    }, [searchCriteria]);

    const clientConfigsQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfigs({
            shouldGetHrpPlans: true,
        }),
        queryFn: () =>
            fetchPlanManagerClientConfigs({ shouldGetHrpPlans: true }),
    });

    useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.plansAll,
        queryFn: () =>
            loadAllPlansGrids({
                fetchJavelinaConfigPage: fetchPlanManagerClientConfig,
                fetchJavelinaBatch: fetchPlanManagerPlansBatch,
                fetchHealthEdgeConfigPage: fetchPlanManagerHealthEdgeConfig,
                fetchHealthEdgeBatch: fetchPlanManagerHealthEdgePlansBatch,
            }),
        ...PLAN_MANAGER_LONG_FETCH_QUERY_OPTIONS,
    });

    const networkConfigs = useMemo(
        () => mapClientConfigsToNetworkRows(clientConfigsQuery.data ?? []),
        [clientConfigsQuery.data],
    );

    const visibleNetworkConfigs = useMemo(() => {
        const matchingRows = filterNetworkConfigsBySearchCriteria(
            networkConfigs,
            appliedSearchCriteria,
        );

        return sortNetworkConfigs(
            matchingRows,
            sortedField,
            isSortDescending,
        );
    }, [
        networkConfigs,
        appliedSearchCriteria,
        sortedField,
        isSortDescending,
    ]);

    const handleSort = (field) => {
        const nextSort = cycleNetworkListSort(
            sortedField,
            isSortDescending,
            field,
        );

        setSortedField(nextSort.sortedField);
        setIsSortDescending(nextSort.isSortDescending);
    };

    const isLoading = clientConfigsQuery.isLoading;
    const isError = clientConfigsQuery.isError;
    const loadErrorMessage =
        clientConfigsQuery.error?.message ?? NETWORKS_LOAD_ERROR_MESSAGE;

    return {
        searchCriteria,
        setSearchCriteria,
        visibleNetworkConfigs,
        appliedSearchCriteria,
        sortedField,
        isSortDescending,
        handleSort,
        isLoading,
        isError,
        loadErrorMessage,
    };
}
